import { learningLessons, foundationLessonId } from "./learningCurriculum.js";
import { practiceDate, shiftPracticeDate } from "./chessActivityCalendar.js";
import { openingLessonManifest } from "./openingLessonManifest.js";
export function lessonTransferBounds(id) {
    const foundation = learningLessons.find((l) => foundationLessonId(l.slug) === id);
    return foundation
        ? { practice: foundation.exercises.map(() => 1), challenge: [] }
        : Object.prototype.hasOwnProperty.call(openingLessonManifest, id)
            ? openingLessonManifest[id]
            : null;
}
/** Imported attendance is self-reported browser history, never competitive credit. */
export function validateGuestProgress(value, now = new Date()) {
    const v = value;
    const invalid = () => {
        throw new Error("Invalid guest progress");
    };
    if (!v ||
        JSON.stringify(v).length > 131072 ||
        v.version !== 1 ||
        typeof v.transferId !== "string" ||
        !/^[a-f0-9]{32}$/.test(v.transferId) ||
        typeof v.timezone !== "string" ||
        v.timezone.length > 64)
        return invalid();
    try {
        new Intl.DateTimeFormat("en", { timeZone: v.timezone }).format(now);
    }
    catch (_a) {
        return invalid();
    }
    const today = practiceDate(now, v.timezone);
    const earliest = shiftPracticeDate(today, -3650);
    if (!Array.isArray(v.dates) ||
        v.dates.length > 3651 ||
        !Array.isArray(v.lessons) ||
        v.lessons.length > 50)
        return invalid();
    for (const date of v.dates) {
        if (typeof date !== "string" ||
            !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
            date < earliest ||
            date > today)
            return invalid();
        const parsed = new Date(`${date}T12:00:00Z`);
        if (!Number.isFinite(parsed.getTime()) ||
            parsed.toISOString().slice(0, 10) !== date)
            return invalid();
    }
    const seen = new Set();
    const lessons = v.lessons.map((l) => {
        if (!l || typeof l.lessonId !== "string" || seen.has(l.lessonId))
            return invalid();
        seen.add(l.lessonId);
        const bounds = lessonTransferBounds(l.lessonId);
        if (!bounds || typeof l.challengeCompleted !== "boolean")
            return invalid();
        const indices = (input, totals) => {
            if (!Array.isArray(input) ||
                input.length > totals.length ||
                input.some((n) => !Number.isSafeInteger(n) || n < 0 || n >= totals.length))
                return invalid();
            return [...new Set(input)].sort((a, b) => a - b);
        };
        const partial = (input, totals) => {
            if (!Array.isArray(input) ||
                input.length > totals.length ||
                input.some((n, i) => !Number.isSafeInteger(n) || n < 0 || n > totals[i]))
                return invalid();
            return totals.map((_, i) => input[i] || 0);
        };
        const completedLines = indices(l.completedLines, bounds.practice);
        const completedChallengeLines = indices(l.completedChallengeLines, bounds.challenge);
        const practiceLineProgress = partial(l.practiceLineProgress, bounds.practice);
        const challengeLineProgress = partial(l.challengeLineProgress, bounds.challenge);
        completedLines.forEach((i) => (practiceLineProgress[i] = bounds.practice[i]));
        completedChallengeLines.forEach((i) => (challengeLineProgress[i] = bounds.challenge[i]));
        practiceLineProgress.forEach((n, i) => {
            if (n === bounds.practice[i] && !completedLines.includes(i))
                completedLines.push(i);
        });
        challengeLineProgress.forEach((n, i) => {
            if (n === bounds.challenge[i] && !completedChallengeLines.includes(i))
                completedChallengeLines.push(i);
        });
        return {
            lessonId: l.lessonId,
            completedLines: completedLines.sort((a, b) => a - b),
            completedChallengeLines: completedChallengeLines.sort((a, b) => a - b),
            practiceLineProgress,
            challengeLineProgress,
            challengeCompleted: bounds.challenge.length > 0 &&
                completedChallengeLines.length === bounds.challenge.length,
        };
    });
    return {
        version: 1,
        transferId: v.transferId,
        timezone: v.timezone,
        dates: [...new Set(v.dates)].sort(),
        lessons,
    };
}
export function mergeTransferredLesson(previous, incoming) {
    const union = (a, b) => [...new Set([...a, ...b])].sort((x, y) => x - y);
    const max = (a, b) => Array.from({ length: Math.max(a.length, b.length) }, (_, i) => Math.max(a[i] || 0, b[i] || 0));
    return Object.assign(Object.assign({}, incoming), { completedLines: union((previous === null || previous === void 0 ? void 0 : previous.completedLines) || [], incoming.completedLines), completedChallengeLines: union((previous === null || previous === void 0 ? void 0 : previous.completedChallengeLines) || [], incoming.completedChallengeLines), practiceLineProgress: max((previous === null || previous === void 0 ? void 0 : previous.practiceLineProgress) || [], incoming.practiceLineProgress), challengeLineProgress: max((previous === null || previous === void 0 ? void 0 : previous.challengeLineProgress) || [], incoming.challengeLineProgress), challengeCompleted: !!(previous === null || previous === void 0 ? void 0 : previous.challengeCompleted) || incoming.challengeCompleted });
}
//# sourceMappingURL=guestProgressTransfer.js.map